/**
 * ÇEVİRİ DETAY SAYFASI — çeviri kartına tıklanınca açılır:
 * belirgin başlık/meta, kelime eşleme (ülke + GLOBAL hacim), hedef pazar SERP bulguları,
 * link inşası bekleyenler, GEO taktikleri, yan yana içerik (hover ile cümle eşleştirme),
 * takvime ekleme + dışa aktarma. Görseller ORİJİNAL URL'leriyle aynı konumda kalır.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { Link, useRoute, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MiniMd, mdToHtml } from "@/pages/content-translate";
import { ArrowLeft, Check, Link2, Download, Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c" };
const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/";

type TranslationData = {
  sourceUrl: string; sourceTitle: string; sourceLang: string | null; targetLang: string;
  keywordMap: { source: string; candidates: { keyword: string; volume: number; difficulty: number; cpc: number; globalVolume?: number; volumeSource?: "ads" | "clickstream" }[]; chosen: string }[];
  semanticKeywords: { term: string; volume: number }[];
  translatedTitle: string; translatedMeta: string; translatedContent: string;
  linkMapping: { anchor: string; sourceUrl: string; targetUrl: string | null; note: string; context?: string }[];
  geoNotes: string[]; clusterName: string | null; sourceKind?: string;
  completeness?: { sourceHeadings: number; translatedHeadings: number; sourceChars: number; translatedChars: number };
  contentScore?: {
    score: number; wordCount: number; density: number; keywordCount: number;
    headings: number; images: number; internalLinks: number; externalLinks: number;
    checks: { label: string; ok: boolean; note?: string }[];
  };
  marketInsights?: {
    keyword: string;
    organic: { title: string; url: string; domain: string }[];
    paa: string[];
    aiOverviewAnswer?: string;
    aiOverviewSources: { domain: string; url: string }[];
  };
};
type Row = { id: number; domainId: number; pageId: number; targetLang: string; runAt: string; data: TranslationData; sourceContent?: string | null };

// Paragraf/blok bazlı bölme — hover eşleştirme için (tablolar tek blok kalır)
function splitBlocks(md: string): string[] {
  return md.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
}

export default function TranslationDetail() {
  const [, params] = useRoute("/translation/:id");
  const id = Number(params?.id ?? 0);
  const [, setLocation] = useLocation();
  const [copied, setCopied] = useState<string | null>(null);
  const [addedCal, setAddedCal] = useState(false);

  const { data: row } = useQuery({
    queryKey: ["translation", id],
    enabled: id > 0,
    queryFn: async (): Promise<Row | null> => {
      const r = await fetch(`/api/content-translations/${id}`);
      return r.ok ? r.json() : null;
    },
  });
  const d = row?.data ?? null;

  const copyText = async (key: string, text: string, html = false) => {
    try {
      if (html && navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([mdToHtml(text)], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
      } else await navigator.clipboard.writeText(text);
      setCopied(key); setTimeout(() => setCopied(null), 1500);
    } catch { /* pano izni yok */ }
  };
  // WORDPRESS'E GÖNDER (2026-09-21): çeviri, kaynak yazıya BAĞLI çeviri olarak gider
  // (WPML/Polylang çeviri grubu) — dil değiştiricide görünür, hreflang üretilir.
  const [wpBusy, setWpBusy] = useState(false);
  const wpDurum = useQuery<{ bagli: boolean }>({
    queryKey: ["wp-durum", row?.domainId],
    enabled: !!row?.domainId,
    staleTime: 60_000,
    queryFn: async () => (await fetch(`/api/domains/${row!.domainId}/wp`)).json(),
  });
  const wpGonder = async (yayinla: boolean) => {
    if (!row?.domainId || wpBusy) return;
    setWpBusy(true);
    try {
      const r2 = await fetch(`/api/domains/${row.domainId}/wp/ceviri-gonder`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ ceviriId: id, yayinla }),
      });
      const j = await r2.json();
      if (!r2.ok) throw new Error(j.error ?? "Gönderilemedi");
      if (j.dilUyarisi) alert(`⚠️ ${j.dilUyarisi}`);
      alert(`${yayinla ? "Yayınlandı" : "Taslak olarak gönderildi"} ✓${j.dil ? ` · Dil: ${String(j.dil).toUpperCase()}` : ""}${j.yazarAdi ? ` · Yazar: ${j.yazarAdi}` : ""}${j.wpLink ? `\n${j.wpLink}` : ""}`);
    } catch (e) { alert(String(e instanceof Error ? e.message : e)); }
    finally { setWpBusy(false); }
  };

  const downloadWord = () => {
    if (!d) return;
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>${d.translatedTitle}</title></head><body><h1>${d.translatedTitle}</h1>${mdToHtml(d.translatedContent)}</body></html>`;
    const blob = new Blob(["﻿", html], { type: "application/msword" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ceviri-${d.targetLang}.doc`;
    a.click(); URL.revokeObjectURL(a.href);
  };
  const del = async () => {
    if (!row || !window.confirm("Bu çeviri silinsin mi?")) return;
    await fetch(`/api/content-translations/${row.id}`, { method: "DELETE" });
    setLocation("/content-translate");
  };

  // İçerik Takvimi — çevrilen başlık + seçilen kelimeler (hacimleriyle) plana eklenir
  const addToCalendar = async () => {
    if (!row || !d) return;
    const firstImg = d.translatedContent.match(/!\[[^\]]*\]\(([^)\s]+)\)/)?.[1];
    const kws = d.keywordMap.slice(0, 7).map((m, i) => {
      const c = m.candidates.find((x) => x.keyword === m.chosen) ?? m.candidates[0];
      return { keyword: m.chosen, usa: 0, global: c?.globalVolume ?? c?.volume ?? 0, role: i === 0 ? "ana" : "destek" };
    });
    await fetch(`/api/domains/${row.domainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title: d.translatedTitle, plannedDate: new Date().toISOString().slice(0, 10), contentType: "blog",
        coverUrl: firstImg, targetUrl: d.sourceUrl,
        notes: `${d.translatedMeta}\n\nÇeviri: ${(d.sourceLang ?? "?").toUpperCase()} → ${d.targetLang.toUpperCase()}${d.clusterName ? ` · ${d.clusterName}` : ""}\n(Çeviri Ajanı içeriği: /translation/${row.id})`,
        keywords: kws,
      }),
    });
    setAddedCal(true);
  };

  // ── TEKRAR ÇEVİR — aynı sayfa+dil yeniden çevrilir (üzerine yazılır), bitince sayfa yenilenir
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [retranslating, setRetranslating] = useState(false);
  const sawRunning = useRef(false);
  const retranslate = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${row!.domainId}/content-translate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId: row!.pageId, targetLang: row!.targetLang }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Çeviri başlatılamadı");
      return r.json();
    },
    onSuccess: () => {
      sawRunning.current = false; setRetranslating(true);
      toast({ title: "🔄 Yeniden çeviri başlatıldı", description: "1-2 dk sürer — bitince bu sayfa otomatik yenilenir." });
    },
    onError: (e) => toast({ title: "❌ Başlatılamadı", description: (e as Error).message, variant: "destructive" }),
  });
  const { data: runsData } = useQuery({
    queryKey: ["translation-runs-detail", row?.domainId],
    enabled: !!row && retranslating,
    refetchInterval: 4000,
    queryFn: async (): Promise<{ runs: { key: string; running: boolean; error: string | null }[] }> => {
      try { const r = await fetch(`/api/domains/${row!.domainId}/translation-runs`); return r.ok ? await r.json() : { runs: [] }; } catch { return { runs: [] }; }
    },
  });
  useEffect(() => {
    if (!retranslating || !row || !runsData) return;
    const key = `${row.pageId}:${row.targetLang}`;
    const run = runsData.runs.find((r) => r.key === key);
    if (run?.running) { sawRunning.current = true; return; }
    if (run && !run.running && run.error) {
      setRetranslating(false);
      toast({ title: "❌ Çeviri başarısız", description: run.error, variant: "destructive" });
      return;
    }
    if (sawRunning.current || (!run && runsData.runs !== undefined)) {
      setRetranslating(false);
      toast({ title: "✅ Çeviri güncellendi", description: "Yeni çeviri yüklendi." });
      queryClient.invalidateQueries({ queryKey: ["translation", id] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsData, retranslating]);

  const pending = (d?.linkMapping ?? []).filter((l) => !l.targetUrl);
  const mapped = (d?.linkMapping ?? []).filter((l) => l.targetUrl);
  const isRtl = d ? ["ar", "he"].includes(d.targetLang) : false;
  const mi = d?.marketInsights;

  // ── HOVER EŞLEŞTİRME (Google Translate tarzı): çeviride bir bloğun üzerine gelince
  // kaynaktaki karşılık blok vurgulanır + görünüme kaydırılır (oran bazlı hizalama)
  const srcBlocks = useMemo(() => splitBlocks(row?.sourceContent ?? ""), [row?.sourceContent]);
  const trBlocks = useMemo(() => splitBlocks(d?.translatedContent ?? ""), [d?.translatedContent]);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const srcIdxFor = (i: number) => trBlocks.length <= 1 ? 0 : Math.min(srcBlocks.length - 1, Math.round((i * (srcBlocks.length - 1)) / (trBlocks.length - 1)));
  const activeSrc = hoverIdx != null ? srcIdxFor(hoverIdx) : null;
  useEffect(() => {
    if (activeSrc != null) document.getElementById(`src-blk-${id}-${activeSrc}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeSrc, id]);
  const hl = { background: "rgba(139,92,246,0.10)", boxShadow: "0 0 0 1px rgba(139,92,246,0.35)" };

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Link href="/content-translate"><Button size="sm" variant="ghost" className="gap-1.5"><ArrowLeft className="w-4 h-4" /> Çeviri Ajanı</Button></Link>
        {d && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" disabled={retranslating || retranslate.isPending} className="text-xs gap-1 text-white" style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}
              onClick={() => { if (window.confirm("Bu sayfa yeniden çevrilsin mi? Mevcut çevirinin üzerine yazılır.")) retranslate.mutate(); }}>
              {retranslating || retranslate.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Çevriliyor…</> : <><RefreshCw className="w-3.5 h-3.5" /> Tekrar Çevir</>}
            </Button>
            <Button size="sm" variant={addedCal ? "outline" : "secondary"} disabled={addedCal} className="text-xs gap-1" onClick={addToCalendar}>
              {addedCal ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Takvimde</> : "📅 Takvime Ekle"}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => copyText("md", d.translatedContent)}>{copied === "md" ? <Check className="w-3.5 h-3.5" /> : null} Markdown</Button>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => copyText("html", d.translatedContent, true)}>{copied === "html" ? <Check className="w-3.5 h-3.5" /> : null} HTML Kopyala</Button>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={downloadWord}><Download className="w-3.5 h-3.5" /> Word İndir</Button>
            {wpDurum.data?.bagli && (<>
              <Button size="sm" disabled={wpBusy} className="text-xs gap-1 bg-brand-500 hover:bg-brand-600"
                title="Çeviri, kaynak yazının ÇEVİRİSİ olarak WordPress'e taslak gönderilir (dil değiştiricide görünür)"
                onClick={() => void wpGonder(false)}>
                {wpBusy ? "Gönderiliyor…" : "WordPress'e Gönder (Taslak)"}
              </Button>
              <Button size="sm" variant="outline" disabled={wpBusy} className="text-xs gap-1 text-amber-600 border-amber-500/40"
                title="Doğrudan yayınlar" onClick={() => void wpGonder(true)}>
                Yayınla
              </Button>
            </>)}
            <Button size="sm" variant="ghost" className="text-xs text-red-400 hover:text-red-500" onClick={del}>🗑 Sil</Button>
          </div>
        )}
      </div>

      {!d && <div className="text-center text-sm text-muted-foreground py-20">Yükleniyor…</div>}

      {d && (<>
        <div className="flex items-center gap-2 flex-wrap mb-3 text-[11px]">
          <span className="px-2 py-0.5 rounded-full font-medium" style={{ background: `${C.purple}15`, color: "#d42b29" }}>{(d.sourceLang ?? "?").toUpperCase()} → {d.targetLang.toUpperCase()}</span>
          {d.clusterName && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{d.clusterName}</span>}
          {d.sourceKind === "revize" && <span className="px-2 py-0.5 rounded-full" style={{ background: `${C.green}15`, color: "#039855" }}>⚡ güçlendirilmiş kaynak</span>}
          {d.completeness && (
            <span className="px-2 py-0.5 rounded-full" style={d.completeness.translatedHeadings >= d.completeness.sourceHeadings ? { background: `${C.green}15`, color: "#039855" } : { background: `${C.amber}18`, color: "#b54708" }}>
              📑 {d.completeness.translatedHeadings}/{d.completeness.sourceHeadings} başlık
            </span>
          )}
          <span className="text-muted-foreground">{row ? new Date(row.runAt).toLocaleString("tr-TR") : ""}</span>
          <a href={d.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted text-muted-foreground">Kaynak: {d.sourceTitle || shortUrl(d.sourceUrl)}</a>
        </div>

        {/* ── BELİRGİN BAŞLIK + META — hedef dilde yayınlanacak SEO alanları ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Card style={{ borderColor: `${C.purple}44` }}><CardContent className="p-4">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[10px] font-semibold tracking-wide text-brand-600 dark:text-brand-400">SEO BAŞLIĞI ({d.targetLang.toUpperCase()})</span>
              <span className="flex items-center gap-2">
                <span className="text-[10px] tabular-nums" style={{ color: d.translatedTitle.length > 60 ? "#b54708" : "#a3a3a0" }}>{d.translatedTitle.length}/60</span>
                <button className="text-[10px] underline text-muted-foreground" onClick={() => copyText("title", d.translatedTitle)}>{copied === "title" ? "✓ kopyalandı" : "kopyala"}</button>
              </span>
            </div>
            <h1 className="text-xl font-bold leading-snug" dir={isRtl ? "rtl" : undefined}>{d.translatedTitle}</h1>
          </CardContent></Card>
          <Card style={{ borderColor: `${C.cyan}44` }}><CardContent className="p-4">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[10px] font-semibold tracking-wide text-blue-light-700 dark:text-blue-light-500">META AÇIKLAMASI</span>
              <span className="flex items-center gap-2">
                <span className="text-[10px] tabular-nums" style={{ color: d.translatedMeta.length > 155 ? "#b54708" : "#a3a3a0" }}>{d.translatedMeta.length}/155</span>
                <button className="text-[10px] underline text-muted-foreground" onClick={() => copyText("meta", d.translatedMeta)}>{copied === "meta" ? "✓ kopyalandı" : "kopyala"}</button>
              </span>
            </div>
            <p className="text-sm leading-relaxed" dir={isRtl ? "rtl" : undefined}>{d.translatedMeta}</p>
          </CardContent></Card>
        </div>

        {/* ── İÇERİK SKORU — RankMath/Keyword Surfer tarzı özet data ── */}
        {d.contentScore && (() => {
          const cs = d.contentScore!;
          const col = cs.score >= 80 ? C.green : cs.score >= 60 ? C.amber : C.red;
          return (
            <Card className="mb-4"><CardContent className="p-4">
              <div className="flex items-start gap-5 flex-wrap">
                <div className="flex flex-col items-center justify-center shrink-0 rounded-2xl px-5 py-3" style={{ background: `${col}12`, border: `1px solid ${col}44` }}>
                  <div className="text-[10px] font-semibold tracking-wide text-muted-foreground">İÇERİK SKORU</div>
                  <div className="text-4xl font-bold tabular-nums leading-tight" style={{ color: col }}>{cs.score}</div>
                  <div className="text-[10px] text-muted-foreground">/ 100</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2.5 text-[11px]">
                    <span className="px-2 py-1 rounded-lg" style={{ background: "rgba(0,0,0,0.04)" }}>📝 <b>{cs.wordCount.toLocaleString("tr-TR")}</b> kelime</span>
                    <span className="px-2 py-1 rounded-lg" style={{ background: "rgba(0,0,0,0.04)" }} title="Ana kelime yoğunluğu">🎯 %<b>{cs.density}</b> yoğunluk · {cs.keywordCount} geçiş</span>
                    <span className="px-2 py-1 rounded-lg" style={{ background: "rgba(0,0,0,0.04)" }}>📑 <b>{cs.headings}</b> alt başlık</span>
                    <span className="px-2 py-1 rounded-lg" style={{ background: "rgba(0,0,0,0.04)" }}>🖼 <b>{cs.images}</b> görsel</span>
                    <span className="px-2 py-1 rounded-lg" style={{ background: "rgba(0,0,0,0.04)" }}>🔗 <b>{cs.internalLinks}</b> iç · <b>{cs.externalLinks}</b> dış link</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
                    {cs.checks.map((ch, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <span style={{ color: ch.ok ? C.green : "#cbd5e1" }}>{ch.ok ? "✓" : "✗"}</span>
                        <span className={ch.ok ? "" : "text-muted-foreground"}>{ch.label}{ch.note && <span className="text-muted-foreground"> — {ch.note}</span>}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent></Card>
          );
        })()}

        {/* Kelime eşleme tablosu — hedef ülke + GLOBAL hacim */}
        {d.keywordMap.length > 0 && (
          <Card className="mb-4"><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-1.5 text-blue-light-700 dark:text-blue-light-500">🔑 Pazar-Doğrulamalı Kelime Eşlemesi</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[680px]">
                <thead><tr className="text-[10px] text-muted-foreground text-left border-b"><th className="py-1 pr-3">Kaynak</th><th className="py-1 pr-3">Seçilen ({d.targetLang.toUpperCase()})</th><th className="py-1 pr-2 text-right">Hacim (ülke)</th><th className="py-1 pr-2 text-right">Global</th><th className="py-1 pr-2 text-right">KD</th><th className="py-1">Diğer adaylar</th></tr></thead>
                <tbody>{d.keywordMap.map((m, i) => {
                  const c = m.candidates.find((x) => x.keyword === m.chosen) ?? m.candidates[0];
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-3">{m.source}</td>
                      <td className="py-1 pr-3 font-semibold text-blue-light-700 dark:text-blue-light-500">{m.chosen}</td>
                      <td className="py-1 pr-2 text-right tabular-nums" style={{ color: (c?.volume ?? 0) > 0 ? C.green : "#a3a3a0" }} title={c?.volumeSource === "clickstream" ? "Clickstream tahmini — Google Ads bu kelimede veri paylaşmıyor (sağlık/estetik kısıtlaması)" : undefined}>{(c?.volume ?? 0) > 0 ? `${c!.volumeSource === "clickstream" ? "≈" : ""}${fmt(c!.volume)}/ay` : "—"}</td>
                      <td className="py-1 pr-2 text-right tabular-nums" style={{ color: (c?.globalVolume ?? 0) > 0 ? "#b12220" : "#a3a3a0" }} title="Dünya geneli aylık arama">{(c?.globalVolume ?? 0) > 0 ? `${fmt(c!.globalVolume!)}/ay` : "—"}</td>
                      <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{(c?.difficulty ?? 0) > 0 ? c!.difficulty : "—"}</td>
                      <td className="py-1 text-muted-foreground truncate max-w-[240px]">{m.candidates.filter((x) => x.keyword !== m.chosen).slice(0, 3).map((x) => `${x.keyword}${(x.volume || x.globalVolume) ? ` (${fmt(x.volume || x.globalVolume || 0)})` : ""}`).join(", ") || "—"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            {d.keywordMap.every((m) => (m.candidates.find((x) => x.keyword === m.chosen)?.volume ?? 0) === 0) && (
              <p className="text-[10px] text-muted-foreground mt-2">Ülke hacmi 0 görünen kelimelerde Global sütununa bak — niş pazarlarda Google Ads ülke verisi eksik olabilir.</p>
            )}
          </CardContent></Card>
        )}

        {/* ── HEDEF PAZAR BULGULARI — SERP rakipleri + PAA + AI Overview ── */}
        {mi && (mi.organic.length > 0 || mi.paa.length > 0 || mi.aiOverviewSources.length > 0) && (
          <Card className="mb-4" style={{ borderColor: `${C.amber}44` }}><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-warning-700 dark:text-warning-400">🏆 Hedef Pazar Bulguları — "{mi.keyword}" ({d.targetLang.toUpperCase()})</div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
              {mi.organic.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-muted-foreground mb-1.5">SERP'TE SIRALANANLARIN YAPTIĞI</div>
                  {mi.organic.map((o, i) => (
                    <div key={i} className="mb-1.5 min-w-0">
                      <span className="tabular-nums text-muted-foreground mr-1">{i + 1}.</span>
                      <a href={o.url} target="_blank" rel="noopener noreferrer" className="font-medium underline decoration-dotted hover:text-cyan-600">{o.domain}</a>
                      <div className="text-muted-foreground truncate" title={o.title}>{o.title}</div>
                    </div>
                  ))}
                </div>
              )}
              {mi.paa.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-muted-foreground mb-1.5">HALK BUNLARI SORUYOR (PAA)</div>
                  {mi.paa.map((q, i) => <div key={i} className="mb-1 text-muted-foreground">❓ {q}</div>)}
                </div>
              )}
              {(mi.aiOverviewSources.length > 0 || mi.aiOverviewAnswer) && (
                <div>
                  <div className="text-[10px] font-semibold text-muted-foreground mb-1.5">AI OVERVIEW'DE KİMLER VAR</div>
                  {mi.aiOverviewSources.length > 0
                    ? mi.aiOverviewSources.map((s, i) => (
                      <div key={i} className="mb-1"><a href={s.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted hover:text-cyan-600 text-brand-600 dark:text-brand-400">{s.domain}</a></div>
                    ))
                    : <div className="text-muted-foreground mb-1">Bu sorguda AI Overview kaynağı görünmüyor.</div>}
                  {mi.aiOverviewAnswer && <div className="text-[10px] text-muted-foreground italic mt-1.5 line-clamp-4">"{mi.aiOverviewAnswer}"</div>}
                </div>
              )}
            </div>
          </CardContent></Card>
        )}

        {/* Link inşası bekleyenler + eşlenen linkler */}
        {(pending.length > 0 || mapped.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 text-xs">
            {pending.length > 0 && (
              <Card style={{ borderColor: `${C.amber}55` }}><CardContent className="p-4">
                <div className="text-[11px] font-semibold mb-1.5 flex items-center gap-1.5 text-warning-700 dark:text-warning-400"><Link2 className="w-3.5 h-3.5" /> Link İnşası Bekleyenler ({pending.length})</div>
                <p className="text-[10px] text-muted-foreground mb-2">Hedef dilde karşılık sayfa yok — anchor metinde kalın duruyor; o sayfa çevrilince link eklenecek.</p>
                {pending.map((l, i) => (
                  <div key={i} className="mb-1.5">
                    <b className="text-warning-700 dark:text-warning-400">{l.anchor}</b> <span className="text-muted-foreground">· {l.note}</span>
                    {l.context && <div className="text-[10px] text-muted-foreground italic mt-0.5">"…{l.context}…"</div>}
                  </div>
                ))}
              </CardContent></Card>
            )}
            {mapped.length > 0 && (
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold mb-1.5 text-success-600 dark:text-success-500">🔗 Mevcut Sayfalara Eşlenen Linkler ({mapped.length})</div>
                <p className="text-[10px] text-muted-foreground mb-2">Sadece hedef dildeki ({d.targetLang.toUpperCase()}) sayfalara link verilir — kaynak dildeki sayfalar otomatik ayıklanır.</p>
                {mapped.map((l, i) => (
                  <div key={i} className="mb-1 truncate"><span style={{ color: C.green }}>"{l.anchor}"</span> → <a href={l.targetUrl!} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted text-muted-foreground">{shortUrl(l.targetUrl!)}</a></div>
                ))}
              </CardContent></Card>
            )}
          </div>
        )}

        {/* GEO taktikleri */}
        {d.geoNotes.length > 0 && (
          <Card className="mb-4"><CardContent className="p-4 text-xs">
            <div className="text-[11px] font-semibold mb-1.5 text-brand-600 dark:text-brand-400">🤖 Uygulanan GEO/AEO Taktikleri</div>
            {d.geoNotes.map((g, i) => <div key={i} className="text-muted-foreground mb-1">• {g}</div>)}
          </CardContent></Card>
        )}

        {/* İçerik — YAN YANA görünüm (Google Translate tarzı): solda kaynak, sağda çeviri.
            Çeviride bir paragrafın üzerine gelince kaynaktaki karşılığı vurgulanır. */}
        {row?.sourceContent ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardContent className="p-0">
              <div className="sticky top-0 z-10 px-4 py-2 border-b text-[11px] font-semibold rounded-t-xl" style={{ background: "rgba(0,0,0,0.03)", color: "#70706c" }}>📄 KAYNAK ({(d.sourceLang ?? "?").toUpperCase()}) — orijinal içerik</div>
              <div className="p-5 max-h-[80vh] overflow-y-auto opacity-80">
                {srcBlocks.map((b, i) => (
                  <div key={i} id={`src-blk-${id}-${i}`} className="rounded-md px-1.5 -mx-1.5 py-0.5 transition-colors" style={activeSrc === i ? hl : undefined}>
                    <MiniMd text={b} />
                  </div>
                ))}
              </div>
            </CardContent></Card>
            <Card style={{ borderColor: `${C.purple}44` }}><CardContent className="p-0">
              <div className="sticky top-0 z-10 px-4 py-2 border-b text-[11px] font-semibold rounded-t-xl flex items-center justify-between gap-2" style={{ background: `${C.purple}0d`, color: "#d42b29" }}>
                <span>🌍 ÇEVİRİ ({d.targetLang.toUpperCase()}) — güçlendirilmiş yerelleştirme</span>
                <span className="text-[10px] font-normal opacity-70">paragrafın üzerine gel → kaynağı vurgular</span>
              </div>
              <div className="p-5 max-h-[80vh] overflow-y-auto" dir={isRtl ? "rtl" : undefined} onMouseLeave={() => setHoverIdx(null)}>
                {trBlocks.map((b, i) => (
                  <div key={i} onMouseEnter={() => setHoverIdx(i)} className="rounded-md px-1.5 -mx-1.5 py-0.5 transition-colors cursor-default" style={hoverIdx === i ? hl : undefined}>
                    <MiniMd text={b} />
                  </div>
                ))}
              </div>
            </CardContent></Card>
          </div>
        ) : (
          <Card><CardContent className="p-6" dir={isRtl ? "rtl" : undefined}>
            <MiniMd text={d.translatedContent} />
          </CardContent></Card>
        )}
      </>)}
    </div>
  );
}
